import React, { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  Box,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ChartResponse } from 'src/api/Api';

interface ActivityTrendChartProps {
  chartData: ChartResponse;
  deploymentName: string;
  allowExporting?: boolean;
}

const ActivityTrendChart: React.FC<ActivityTrendChartProps> = ({ 
  chartData, 
  deploymentName, 
  allowExporting = false,
}) => {
  const [selectedSpecies, setSelectedSpecies] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  // Get activity trend data for the first (and only) deployment
  const activityTrend = chartData.comparison[0]?.activity_trend;

  // Get available species from activity trend data
  const availableSpecies = useMemo(() => {
    if (!activityTrend?.species_trends) return [];
    return Object.keys(activityTrend.species_trends);
  }, [activityTrend]);

  // Get time slots from activity trend or fallback to chart data
  const timeSlots = useMemo(() => {
    return activityTrend?.time_slots || chartData.time_slots || [];
  }, [activityTrend, chartData]);

  const options = useMemo(() => {
    if (!activityTrend || !timeSlots.length) return null;

    const speciesTrends = activityTrend.species_trends;

    let series: Highcharts.SeriesOptionsType[] = [];

    if (selectedSpecies === 'all') {
      // Show all species
      series = availableSpecies.map((species, index) => {
        const speciesData = speciesTrends[species];
        // Handle both array format and object with counts property
        let data: number[] = [];
        if (Array.isArray(speciesData)) {
          data = speciesData.map((v: number | null | undefined) => v ?? 0);
        } else if (speciesData && typeof speciesData === 'object' && 'counts' in speciesData) {
          data = (speciesData.counts as number[]).map((v: number | null | undefined) => v ?? 0);
        }

        return {
          type: 'line',
          name: species,
          data: data,
          color: Highcharts.getOptions().colors?.[index % 10],
          marker: { symbol: 'circle' },
        };
      });
    } else {
      // Show selected species only
      const speciesData = speciesTrends[selectedSpecies];
      // Handle both array format and object with counts property
      let data: number[] = [];
      if (Array.isArray(speciesData)) {
        data = speciesData.map((v: number | null | undefined) => v ?? 0);
      } else if (speciesData && typeof speciesData === 'object' && 'counts' in speciesData) {
        data = (speciesData.counts as number[]).map((v: number | null | undefined) => v ?? 0);
      }

      series = [{
        type: 'line',
        name: selectedSpecies,
        data: data,
        color: '#1f77b4',
        marker: { symbol: 'circle' },
      }];
    }

    return {
      chart: { 
        type: 'line', 
        zoomType: 'x',
        height: 400
      },
      exporting: { enabled: allowExporting },
      title: { 
        text: `Species Activity Trend`,
        style: { fontSize: '16px' }
      },
      xAxis: {
        categories: timeSlots,
        labels: { rotation: -45 },
        title: { text: 'Time of Day' },
      },
      yAxis: { 
        title: { text: 'Activity Level' },
        min: 0
      },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point) {
          return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${(this.y as number).toFixed(2)}</b><br/>`;
        },
      },
      legend: {
        enabled: selectedSpecies === 'all',
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        maxHeight: 100,
      },
      plotOptions: {
        line: {
          marker: {
            radius: 3,
            lineWidth: 1,
            lineColor: '#ffffff'
          }
        }
      },
      series,
    };
  }, [activityTrend, selectedSpecies, availableSpecies, deploymentName]);

  const handleSpeciesChange = (species: string) => {
    setLoading(true);
    setSelectedSpecies(species);
    setTimeout(() => setLoading(false), 300);
  };

  if (!activityTrend) {
    return (
      <Box sx={{ 
        height: '500px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        flexDirection: 'column'
      }}>
        <Typography variant="h6" color="textSecondary">
          No Activity Trend Data Available
        </Typography>
        <Typography variant="body2" color="textSecondary">
          This deployment does not have activity trend data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '500px', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {/* Deployment Name */}
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {deploymentName}
        </Typography>

        {/* Species Selection */}
        <Box display="flex" alignItems="center">
            <Select
              value={selectedSpecies}
              onChange={(e) => handleSpeciesChange(e.target.value)}
              label="Species"
              renderValue={(value) => value === 'all' ? 'All Species' : value}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    maxWidth: 300,
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              sx={{
                fontSize: '.8rem',
                textAlign: 'center',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 0,
                p: 0,
                m: 0,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: 'primary.dark',
                },
                '&.Mui-focused': {
                  borderColor: 'primary.main',
                  borderBottomWidth: '2px',
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                },
              }}
            >
              <MenuItem value="all">All Species</MenuItem>
              {availableSpecies.map((species) => (
                <MenuItem key={species} value={species}>
                  {species}
                </MenuItem>
              ))}
            </Select>

          <Tooltip
            title={
              <Box>
                <Typography variant="body2">
                  <b>Activity Trends</b> show the temporal patterns of species activity
                </Typography>
                <Typography variant="body2">
                  Select "All Species" to compare multiple species or choose a specific species
                </Typography>
              </Box>
            }
            placement="left"
            arrow
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '85%' 
          }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2 }}>Loading activity trends...</Typography>
        </Box>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Box>
  );
};

export default ActivityTrendChart;
